import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  Divider,
  Button,
  TextField,
  Grid,
  CardContent,
  Card,
  Breadcrumbs,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import Map from "../waterLevelRecords/selectLocationMap/Map";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { DateTimePicker } from "@mui/x-date-pickers";
import { format, parseISO } from "date-fns";

import { ROUTES } from "../../constants";
import { useWaterQualityForm } from "./useWaterQualityForm";

const DEFAULT_FORM_VALUES = {
  ndx: null,
  workorder: "",
  client: "",
  project: "",
  date_received: null,
  sample_id: "",
  client_sample_id: "",
  collection_date: null,
  matrix: "",
  test_no: "",
  batch_id: null,
  prep_date: null,
  anal_date: null,
  parameter_ndx: null,
  analyte: "",
  df: null,
  units: "",
  rpt_val: "",
  pql: null,
  mdl: null,
  qual: "",
  storet: "",
  test_name: "",
  well_ndx: null,
};

export default function WaterQuality() {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const wellNdx = parseInt(queryParams.get("well_ndx"), 10) || null;

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showOnlyWithWQ, setShowOnlyWithWQ] = useState(true); // Default to showing only wells with WQ data
  const [selectedStagingRowIds, setSelectedStagingRowIds] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmUploadOpen, setConfirmUploadOpen] = useState(false);
  const [processingRecordId, setProcessingRecordId] = useState(null);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [rejectConfirmData, setRejectConfirmData] = useState(null);

  const {
    listWqWellsQuery,
    wqParametersQuery,
    waterQualityEntriesQuery,
    saveWaterQualityMutation,
    deleteWaterQualityMutation,
    uploadCsvMutation,
    stagingRecordsQuery,
    approveStagingMutation,
    bulkApproveStagingMutation,
  } = useWaterQualityForm(wellNdx);

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { isSubmitting, isDirty, errors },
  } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const selectedParameterNdx = useWatch({ control, name: "parameter_ndx" });

  const {
    data: wellsData = [],
    isLoading: isLoadingWells,
    error: errorWells,
  } = listWqWellsQuery;

  const {
    data: parametersData = [],
    isLoading: isLoadingParameters,
    error: errorParameters,
  } = wqParametersQuery;

  const filteredWells = useMemo(() => {
    if (!showOnlyWithWQ) return wellsData;
    return wellsData.filter((w) => w.has_wq_data);
  }, [wellsData, showOnlyWithWQ]);

  const {
    data: waterQualityData = [],
    isLoading: isLoadingWaterQuality,
    error: errorWaterQuality,
  } = waterQualityEntriesQuery;

  const {
    data: stagingData = [],
    isLoading: isLoadingStaging,
    error: errorStaging,
  } = stagingRecordsQuery;

  // Effect to update derived values when parameter is selected
  useEffect(() => {
    if (selectedParameterNdx && parametersData.length > 0) {
      const selectedParam = parametersData.find(
        (p) => p.ndx === selectedParameterNdx
      );
      if (selectedParam) {
        setValue("analyte", selectedParam.parameter_desc || "");
        setValue("units", selectedParam.unit || "");
        setValue("df", selectedParam.df || null);
      }
    }
  }, [selectedParameterNdx, parametersData, setValue]);

  const loading =
    isLoadingWells ||
    isLoadingWaterQuality ||
    isLoadingParameters ||
    isLoadingStaging;
  const error =
    errorWells || errorWaterQuality || errorParameters || errorStaging;

  const columns = useMemo(() => {
    return [
      { field: "workorder", headerName: "Work Order", minWidth: 120 },
      { field: "client", headerName: "Client", minWidth: 120 },
      { field: "project", headerName: "Project", minWidth: 120 },
      {
        field: "date_received",
        headerName: "Date Received",
        minWidth: 130,
        valueFormatter: (value) => {
          if (!value) return "";
          return format(parseISO(value), "MM/dd/yyyy");
        },
      },
      { field: "sample_id", headerName: "Sample ID", minWidth: 120 },
      {
        field: "client_sample_id",
        headerName: "Client Sample ID",
        minWidth: 140,
      },
      {
        field: "collection_date",
        headerName: "Collection Date",
        minWidth: 130,
        valueFormatter: (value) => {
          if (!value) return "";
          return format(parseISO(value), "MM/dd/yyyy");
        },
      },
      { field: "matrix", headerName: "Matrix", minWidth: 100 },
      { field: "test_no", headerName: "Test No", minWidth: 100 },
      { field: "batch_id", headerName: "Batch ID", minWidth: 100 },
      {
        field: "prep_date",
        headerName: "Prep Date",
        minWidth: 120,
        valueFormatter: (value) => {
          if (!value) return "";
          return format(parseISO(value), "MM/dd/yyyy");
        },
      },
      {
        field: "anal_date",
        headerName: "Analysis Date",
        minWidth: 130,
        valueFormatter: (value) => {
          if (!value) return "";
          return format(parseISO(value), "MM/dd/yyyy");
        },
      },
      { field: "analyte", headerName: "Analyte", minWidth: 200 },
      { field: "df", headerName: "DF", minWidth: 80 },
      { field: "units", headerName: "Units", minWidth: 80 },
      { field: "rpt_val", headerName: "Result", minWidth: 100 },
      { field: "pql", headerName: "PQL", minWidth: 80 },
      { field: "mdl", headerName: "MDL", minWidth: 80 },
      { field: "qual", headerName: "Qualifier", minWidth: 80 },
      { field: "storet", headerName: "STORET", minWidth: 100 },
      { field: "test_name", headerName: "Test Name", minWidth: 150 },
    ];
  }, []);

  const handleRejectStaging = useCallback(
    async (ndx, event) => {
      event.stopPropagation(); // Prevent row click
      setRejectConfirmData({ type: "single", ndx });
      setRejectConfirmOpen(true);
    },
    [setRejectConfirmData, setRejectConfirmOpen]
  );

  const handleApproveStaging = useCallback(
    async (ndx, event) => {
      event.stopPropagation(); // Prevent row click
      setProcessingRecordId(ndx);
      try {
        await approveStagingMutation.mutateAsync({ ndx, status: "approved" });
      } catch (error) {
        console.error("Approve error:", error);
      } finally {
        setProcessingRecordId(null);
      }
    },
    [approveStagingMutation, setProcessingRecordId]
  );

  const stagingColumns = useMemo(() => {
    return [
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 200,
        sortable: false,
        align: "center",
        headerAlign: "center",
        display: "flex",
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={(event) => handleApproveStaging(params.row.ndx, event)}
              disabled={
                params.row.status !== "pending" ||
                processingRecordId === params.row.ndx
              }
            >
              {processingRecordId === params.row.ndx
                ? "Approving..."
                : "Approve"}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={(event) => handleRejectStaging(params.row.ndx, event)}
              disabled={
                params.row.status !== "pending" ||
                processingRecordId === params.row.ndx
              }
            >
              {processingRecordId === params.row.ndx
                ? "Rejecting..."
                : "Reject"}
            </Button>
          </Box>
        ),
      },
      { field: "status", headerName: "Status", minWidth: 100 },
      { field: "well_ndx", headerName: "Well #", minWidth: 80 },
      { field: "client_sample_id", headerName: "Sample ID", minWidth: 120 },
      { field: "workorder", headerName: "Work Order", minWidth: 120 },
      { field: "client", headerName: "Client", minWidth: 120 },
      { field: "project", headerName: "Project", minWidth: 120 },
      {
        field: "collection_date",
        headerName: "Collection Date",
        minWidth: 130,
        valueFormatter: (value) => {
          if (!value) return "";
          return format(parseISO(value), "MM/dd/yyyy");
        },
      },
      { field: "analyte", headerName: "Analyte", minWidth: 200 },
      { field: "rpt_val", headerName: "Result", minWidth: 100 },
      { field: "units", headerName: "Units", minWidth: 80 },
    ];
  }, [handleRejectStaging, handleApproveStaging, processingRecordId]);

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ display: "flex", alignItems: "center", p: 2 }}>
      <Box sx={{ mr: "auto", display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleEditRecord}
          disabled={!selectedRowId || isSubmitting || loading}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteRequest}
          disabled={!selectedRowId || isSubmitting || loading}
        >
          Delete
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleAddRecord}
          disabled={isSubmitting || loading || !wellNdx}
        >
          Add Water Quality Record
        </Button>
        <GridToolbarExport
          slotProps={{
            button: {
              variant: "outlined",
              size: "medium",
            },
          }}
        />
      </Box>
    </GridToolbarContainer>
  );

  const StagingToolbar = () => (
    <GridToolbarContainer sx={{ display: "flex", alignItems: "center", p: 2 }}>
      <Box sx={{ mr: "auto", display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleBulkApprove}
          disabled={
            selectedStagingRowIds.length === 0 ||
            loading ||
            bulkApproveStagingMutation.isLoading
          }
        >
          {bulkApproveStagingMutation.isLoading
            ? "Approving..."
            : `Bulk Approve Selected (${selectedStagingRowIds.length})`}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleBulkReject}
          disabled={
            selectedStagingRowIds.length === 0 ||
            loading ||
            approveStagingMutation.isLoading
          }
        >
          {approveStagingMutation.isLoading
            ? "Rejecting..."
            : `Bulk Reject Selected (${selectedStagingRowIds.length})`}
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <GridToolbarExport
          slotProps={{
            button: {
              variant: "outlined",
              size: "medium",
            },
          }}
        />
      </Box>
    </GridToolbarContainer>
  );

  const setWellNdx = (newWellNdx) => {
    const searchParams = new URLSearchParams(location.search);
    if (newWellNdx) searchParams.set("well_ndx", newWellNdx);
    else searchParams.delete("well_ndx");
    history.push({ search: searchParams.toString() });
  };

  const handleWellChange = (_, newVal) => {
    if (newVal?.well_ndx) {
      setWellNdx(newVal.well_ndx);
    } else {
      setWellNdx(null);
    }
    setSelectedRowId(null);
  };

  const handleRowSelection = (newSelection) => {
    setSelectedRowId(newSelection.length > 0 ? newSelection[0] : null);
  };

  const handleAddRecord = () => {
    setIsCreateMode(true);
    reset({
      ...DEFAULT_FORM_VALUES,
      client: "HTGCD",
      project: "HTGCD Suite",
      workorder: "manual entry",
      sample_id: "manual entry",
      matrix: "Aqueous",
      well_ndx: wellNdx || null,
      collection_date: new Date(),
    });
    setOpenDialog(true);
  };

  const handleEditRecord = () => {
    if (!selectedRowId || !waterQualityData) return;
    const existing = waterQualityData.find((row) => row.ndx === selectedRowId);
    if (!existing) return;

    // Find matching parameter by analyte value
    const matchingParam = parametersData.find(
      (param) => param.parameter_desc === existing.analyte
    );

    setIsCreateMode(false);
    reset({
      ...existing,
      parameter_ndx: matchingParam?.ndx || null,
      // Only parse dates if they actually have values, otherwise keep as null
      collection_date:
        existing.collection_date && existing.collection_date !== ""
          ? parseISO(existing.collection_date)
          : null,
      date_received:
        existing.date_received && existing.date_received !== ""
          ? parseISO(existing.date_received)
          : null,
      prep_date:
        existing.prep_date && existing.prep_date !== ""
          ? parseISO(existing.prep_date)
          : null,
      anal_date:
        existing.anal_date && existing.anal_date !== ""
          ? parseISO(existing.anal_date)
          : null,
    });
    setOpenDialog(true);
  };

  const handleDeleteRequest = () => {
    if (!selectedRowId) return;
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedRowId) return;
    try {
      await deleteWaterQualityMutation.mutateAsync(selectedRowId);
      setSelectedRowId(null);
    } catch (err) {
      console.error("Delete error:", err);
    }
    setDeleteDialogOpen(false);
  };

  const onSubmitForm = async (formValues) => {
    try {
      const payload = {
        ...formValues,
        collection_date: formValues.collection_date
          ? format(formValues.collection_date, "yyyy-MM-dd HH:mm:ss")
          : null,
        date_received: formValues.date_received
          ? format(formValues.date_received, "yyyy-MM-dd HH:mm:ss")
          : null,
        prep_date: formValues.prep_date
          ? format(formValues.prep_date, "yyyy-MM-dd HH:mm:ss")
          : null,
        anal_date: formValues.anal_date
          ? format(formValues.anal_date, "yyyy-MM-dd HH:mm:ss")
          : null,
      };

      await saveWaterQualityMutation.mutateAsync(payload);
      handleCloseDialog();
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset({});
  };

  // File selection handlers
  const handleFileSelection = (file) => {
    if (
      file.type === "text/csv" ||
      file.name.endsWith(".csv") ||
      file.name.endsWith(".xlsx")
    ) {
      setSelectedFile(file);
      setConfirmUploadOpen(true);
    } else {
      alert("Please upload a CSV or XLSX file.");
    }
  };

  // Actual upload handler
  const handleConfirmedUpload = async () => {
    if (!selectedFile) return;

    try {
      await uploadCsvMutation.mutateAsync({ file: selectedFile });
      setConfirmUploadOpen(false);
      setSelectedFile(null);
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  const handleCancelUpload = () => {
    setConfirmUploadOpen(false);
    setSelectedFile(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      handleFileSelection(file);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedStagingRowIds.length === 0) {
      alert("Please select records to approve.");
      return;
    }

    try {
      await bulkApproveStagingMutation.mutateAsync({
        record_ids: selectedStagingRowIds,
      });
      setSelectedStagingRowIds([]);
    } catch (error) {
      console.error("Bulk approve error:", error);
    }
  };

  const handleBulkReject = async () => {
    if (selectedStagingRowIds.length === 0) {
      alert("Please select records to reject.");
      return;
    }

    // Set up confirmation dialog
    setRejectConfirmData({
      type: "bulk",
      recordIds: [...selectedStagingRowIds],
      count: selectedStagingRowIds.length,
    });
    setRejectConfirmOpen(true);
  };

  // Handle confirmed rejection
  const handleConfirmReject = async () => {
    if (!rejectConfirmData) return;

    setRejectConfirmOpen(false);

    try {
      if (rejectConfirmData.type === "single") {
        setProcessingRecordId(rejectConfirmData.ndx);
        await approveStagingMutation.mutateAsync({
          ndx: rejectConfirmData.ndx,
          status: "rejected",
        });
        setProcessingRecordId(null);
      } else {
        // Bulk rejection
        for (const recordId of rejectConfirmData.recordIds) {
          await approveStagingMutation.mutateAsync({
            ndx: recordId,
            status: "rejected",
          });
        }
        setSelectedStagingRowIds([]);
      }
    } catch (error) {
      console.error("Reject error:", error);
      setProcessingRecordId(null);
    } finally {
      setRejectConfirmData(null);
    }
  };

  // Handle cancel rejection
  const handleCancelReject = () => {
    setRejectConfirmOpen(false);
    setRejectConfirmData(null);
  };

  if (error) {
    return <div>An unexpected error has occurred: {String(error)}</div>;
  }

  return (
    <Box>
      <Helmet>
        <title>Water Quality</title>
      </Helmet>

      {/* Page Header */}
      <Typography variant="h3" gutterBottom display="inline">
        Water Quality
      </Typography>
      <Breadcrumbs sx={{ mt: 4 }} aria-label="Breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Water Quality</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 6 }} />

      {/* Page Information */}
      <Card variant="outlined" sx={{ mb: 3, backgroundColor: "#f0f7ff" }}>
        <CardContent>
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            color="primary"
          >
            Water Quality Data Management
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This page provides two workflows for managing water quality test
            results in the system:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <strong>• Batch Upload & Approval:</strong> Process laboratory
                reports containing results for multiple wells, with an approval
                workflow for data validation.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <strong>• Manual Entry & Management:</strong> Direct entry,
                editing, and management of water quality records for individual
                wells.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Section 1: Batch Upload & Approval Workflow */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            color="primary"
          >
            1. Batch Upload & Approval Workflow
          </Typography>

          {/* Upload Subsection */}
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            sx={{ mt: 3 }}
          >
            Upload Lab Reports
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Upload CSV or XLSX files containing water quality test results from
            laboratory reports. Files can include data for multiple wells and
            will be processed for approval.
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>File Requirements:</strong> CSV or XLSX format with these
              exact column headers:
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", display: "block", mt: 1 }}
            >
              WORKORDER | CLIENTID | PROJECTID | DATERECEIVED | SAMPID |
              CLIENTSAMPID | COLLECTIONDATE | MATRIX | TESTNO | BATCHID |
              PREPDATE | ANALDATE | ANALYTE | DF | UNITS | RPTVAL | PQL | MDL |
              QUAL | STORET | TESTNAME
            </Typography>
          </Alert>

          <Box
            sx={{
              border: 2,
              borderColor: dragActive ? "primary.main" : "grey.300",
              borderStyle: "dashed",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              backgroundColor: dragActive ? "action.hover" : "background.paper",
              cursor: "pointer",
              minHeight: 200,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mb: 4,
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Typography variant="h6" gutterBottom>
              Drag and drop your file here
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              or
            </Typography>
            <Button variant="contained" component="label" sx={{ mt: 2 }}>
              Browse Files
              <input
                type="file"
                hidden
                accept=".csv,.xlsx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleFileSelection(file);
                }}
              />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              Supported formats: CSV, XLSX
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Approval Subsection */}
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Pending Approvals Queue
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Review and approve uploaded records before they are added to the
            permanent database. All uploaded data appears here for validation
            and approval.
          </Typography>

          {stagingData.length > 0 ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{stagingData.length} records pending approval</strong>{" "}
                  - Review each record carefully before approving. Approved
                  records will be immediately added to the permanent water
                  quality database.
                </Typography>
              </Alert>
              <Box
                sx={{
                  height: 400,
                  width: "100%",
                }}
              >
                <DataGrid
                  rows={stagingData}
                  columns={stagingColumns}
                  loading={loading}
                  getRowId={(row) => row.ndx}
                  checkboxSelection
                  rowSelectionModel={selectedStagingRowIds}
                  onRowSelectionModelChange={setSelectedStagingRowIds}
                  slots={{ toolbar: StagingToolbar }}
                  sx={{
                    "& .MuiDataGrid-cell:focus": { outline: "none" },
                    "& .MuiDataGrid-cell:focus-within": { outline: "none" },
                    "& .MuiDataGrid-row:hover": { cursor: "pointer" },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "normal",
                    },
                  }}
                />
              </Box>
            </>
          ) : (
            <Alert severity="success">
              <Typography variant="body2">
                No pending approvals. All uploaded records have been processed.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Manual Entry & Management */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            color="primary"
          >
            2. Manual Entry & Management
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Select a specific well to view its water quality history, make edits
            to existing records, or manually add new test results. This option
            is ideal for single-well updates or when reviewing historical data
            for a specific location.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>How to use:</strong> Select a well using the dropdown or
              click on a well in the map. Once selected, you can view all water
              quality records for that well and add new entries manually.
            </Typography>
          </Alert>

          {/* Well Selection Area */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Select Well by Name
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Autocomplete
                  options={filteredWells}
                  getOptionLabel={(option) => {
                    const wellName = option.well_name || "Unnamed Well";
                    const company = option.name_company || "No company listed";
                    const hasName = option.name_first || option.name_last;
                    const name = hasName
                      ? `${option.name_first ?? ""} ${
                          option.name_last ?? ""
                        }`.trim()
                      : "No name provided";
                    const wellLabel = `Well #${option.well_ndx}`;
                    const permitLabel = option.assoc_permit_ndx
                      ? `Permit #${option.assoc_permit_ndx}`
                      : null;

                    return [wellName, wellLabel, permitLabel, company, name]
                      .filter(Boolean)
                      .join(" | ");
                  }}
                  value={wellsData.find((w) => w.well_ndx === wellNdx) || null}
                  onChange={handleWellChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Wells" />
                  )}
                  loading={isLoadingWells}
                  fullWidth
                  slotProps={{
                    popper: {
                      sx: {
                        "& .MuiAutocomplete-listbox": {
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          boxShadow:
                            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                        },
                      },
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showOnlyWithWQ}
                      onChange={() => setShowOnlyWithWQ((prev) => !prev)}
                    />
                  }
                  label={showOnlyWithWQ ? "With WQ Data" : "All Wells"}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Select Well on Map
              </Typography>
              <Box
                sx={{
                  height: 300,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Map
                  data={wellsData}
                  visibleIds={filteredWells.map((w) => w.well_ndx)}
                  setWellNdx={setWellNdx}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Data Grid */}
          {wellNdx ? (
            <Box
              sx={{
                height: 600,
                width: "100%",
              }}
            >
              <DataGrid
                rows={waterQualityData}
                columns={columns}
                loading={loading}
                getRowId={(row) => row.ndx}
                disableMultipleRowSelection
                rowSelectionModel={selectedRowId ? [selectedRowId] : []}
                onRowSelectionModelChange={handleRowSelection}
                slots={{ toolbar: CustomToolbar }}
                sx={{
                  "& .MuiDataGrid-cell:focus": { outline: "none" },
                  "& .MuiDataGrid-cell:focus-within": { outline: "none" },
                  "& .MuiDataGrid-row:hover": { cursor: "pointer" },
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "#d9e7f5",
                  },
                  "& .MuiDataGrid-row.Mui-selected:hover": {
                    backgroundColor: "#c3dbf2",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "normal",
                  },
                }}
              />
            </Box>
          ) : (
            <Alert severity="info" sx={{ my: 2 }}>
              Select a well to view and manage water quality data.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Dialog for Add/Edit */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isCreateMode
            ? "Add Water Quality Entry"
            : "Edit Water Quality Entry"}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <Box
              component="form"
              noValidate
              id="water-quality-form"
              onSubmit={handleSubmit(onSubmitForm)}
              sx={{ mt: 1 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="workorder"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Work Order"
                        fullWidth
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="client_sample_id"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Client Sample ID"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="collection_date"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker
                        label="Collection Date"
                        value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            margin: "normal",
                            fullWidth: true,
                            error: !!errors.collection_date,
                            helperText: errors.collection_date?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="date_received"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker
                        label="Date Received"
                        value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            margin: "normal",
                            fullWidth: true,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="parameter_ndx"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={parametersData}
                        getOptionLabel={(option) => option.parameter_desc || ""}
                        value={
                          parametersData.find((p) => p.ndx === field.value) ||
                          null
                        }
                        onChange={(_, newValue) => {
                          field.onChange(newValue?.ndx ?? null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            margin="normal"
                            label="Parameter"
                            fullWidth
                            required
                          />
                        )}
                        loading={isLoadingParameters}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="analyte"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Analyte (Auto-filled)"
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="rpt_val"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Result Value"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="units"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Units (Auto-filled)"
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="qual"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Qualifier"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="test_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Test Name"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="matrix"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Matrix"
                        fullWidth
                        InputProps={{
                          readOnly: isCreateMode,
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: isCreateMode
                              ? "#f5f5f5"
                              : "inherit",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="anal_date"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker
                        label="Analysis Date"
                        value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            margin: "normal",
                            fullWidth: true,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="prep_date"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker
                        label="Prep Date"
                        value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            margin: "normal",
                            fullWidth: true,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="test_no"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Test Number"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="pql"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        margin="normal"
                        label="PQL"
                        fullWidth
                        type="number"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val === "" ? null : parseFloat(val));
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="mdl"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        margin="normal"
                        label="MDL"
                        fullWidth
                        type="number"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val === "" ? null : parseFloat(val));
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="df"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        margin="normal"
                        label="Dilution Factor (Auto-filled)"
                        fullWidth
                        type="number"
                        value={field.value ?? ""}
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="client"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Client"
                        fullWidth
                        InputProps={{
                          readOnly: isCreateMode,
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: isCreateMode
                              ? "#f5f5f5"
                              : "inherit",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="project"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="Project"
                        fullWidth
                        InputProps={{
                          readOnly: isCreateMode,
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: isCreateMode
                              ? "#f5f5f5"
                              : "inherit",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="storet"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        margin="normal"
                        label="STORET"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="batch_id"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        margin="normal"
                        label="Batch ID"
                        fullWidth
                        type="number"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val === "" ? null : parseInt(val, 10));
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            form="water-quality-form"
            type="submit"
            variant="contained"
            disabled={!isDirty || isSubmitting}
          >
            {isSubmitting ? "Saving..." : isCreateMode ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this water quality record?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Confirmation Dialog */}
      <Dialog
        open={confirmUploadOpen}
        onClose={handleCancelUpload}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm File Upload</DialogTitle>
        <DialogContent>
          {selectedFile && (
            <>
              <Typography variant="body1" gutterBottom>
                Are you ready to upload this file?
              </Typography>

              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2">
                  <strong>File:</strong> {selectedFile.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Size:</strong> {(selectedFile.size / 1024).toFixed(1)}{" "}
                  KB
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> {selectedFile.type || "Unknown"}
                </Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary">
                The file will be processed and the water quality records will
                appear in the Pending Approvals Queue below for your review
                before being added to the database.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpload}>Cancel</Button>
          <Button
            onClick={handleConfirmedUpload}
            variant="contained"
            color="primary"
            disabled={uploadCsvMutation.isLoading}
          >
            {uploadCsvMutation.isLoading ? "Uploading..." : "Upload File"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog
        open={rejectConfirmOpen}
        onClose={handleCancelReject}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Rejection</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {rejectConfirmData?.type === "single"
              ? "Are you sure you want to reject this record?"
              : `Are you sure you want to reject ${
                  rejectConfirmData?.count || 0
                } selected records?`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Rejected records will be permanently
            removed from the approval queue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReject}>Cancel</Button>
          <Button
            onClick={handleConfirmReject}
            variant="contained"
            color="error"
            disabled={approveStagingMutation.isLoading}
          >
            {approveStagingMutation.isLoading ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
